/**
 * The small part of Markdown the assistant writes (paragraphs, headings, bullet and numbered
 * lists, bold, simple tables), parsed into blocks the app renders as React elements. Never HTML:
 * nothing in an answer can inject markup.
 */
export interface Inline {
  readonly text: string;
  readonly bold: boolean;
}

export type Block =
  | { readonly type: 'paragraph'; readonly inline: readonly Inline[] }
  | { readonly type: 'heading'; readonly inline: readonly Inline[] }
  | {
      readonly type: 'list';
      readonly ordered: boolean;
      readonly items: readonly (readonly Inline[])[];
    }
  | {
      readonly type: 'table';
      readonly header: readonly (readonly Inline[])[];
      readonly rows: readonly (readonly (readonly Inline[])[])[];
    };

/** **bold** spans; unmatched markers stay as text. */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  for (const match of text.matchAll(re)) {
    const start = match.index;
    if (start > last) out.push({ text: text.slice(last, start), bold: false });
    out.push({ text: match[1] ?? '', bold: true });
    last = start + match[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), bold: false });
  return out.filter((i) => i.text !== '');
}

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^\s*#{1,6}\s+(.*)$/;
const TABLE_ROW = /^\s*\|(.+)\|\s*$/;
const TABLE_RULE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

const cells = (line: string): Inline[][] =>
  (TABLE_ROW.exec(line)?.[1] ?? '').split('|').map((c) => parseInline(c.trim()));

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'paragraph', inline: parseInline(paragraph.join(' ')) });
      paragraph = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (line.trim() === '') {
      flush();
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({ type: 'heading', inline: parseInline(heading[1] ?? '') });
      continue;
    }
    if (TABLE_ROW.test(line) && TABLE_RULE.test(lines[i + 1] ?? '')) {
      flush();
      const header = cells(line);
      const rows: Inline[][][] = [];
      i += 2;
      while (i < lines.length && TABLE_ROW.test(lines[i] ?? '')) rows.push(cells(lines[i++] ?? ''));
      i--;
      blocks.push({ type: 'table', header, rows });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);
    if (bullet || numbered) {
      flush();
      const ordered = numbered !== null;
      const pattern = ordered ? NUMBERED : BULLET;
      const items: Inline[][] = [];
      while (i < lines.length) {
        const m = pattern.exec(lines[i] ?? '');
        if (!m) break;
        items.push(parseInline(m[1] ?? ''));
        i++;
      }
      i--;
      blocks.push({ type: 'list', ordered, items });
      continue;
    }
    paragraph.push(line.trim());
  }
  flush();
  return blocks;
}

/** Plain text for reading an answer aloud: no markers, table cells joined by commas. */
export function speakable(source: string): string {
  return parseMarkdown(source)
    .map((b) => {
      const plain = (inline: readonly Inline[]) => inline.map((i) => i.text).join('');
      switch (b.type) {
        case 'paragraph':
        case 'heading':
          return plain(b.inline);
        case 'list':
          return b.items.map(plain).join('. ');
        case 'table':
          return b.rows.map((r) => r.map(plain).join('، ')).join('. ');
      }
    })
    .join('\n');
}
